import { useEffect, useState, type FunctionComponent } from "react";
import { Alert, Descriptions, Form, Input, InputNumber, message, Modal, Skeleton } from "antd";
import dayjs from "dayjs";

import {
  PayoffErrorCode,
  type PayoffOfflineSettlementPreview,
  type PayoffSettlementWarning,
  type PayoffSettleOfflineResponse,
} from "@fitness-app/data-models/entities/Payoff";
import { type SubscriptionProductClient } from "@fitness-app/data-models/entities/ProductClient";

import { productApi } from "~/store/initializeStore";

interface OwnProps {
  productClient: SubscriptionProductClient;
  open: boolean;
  onClose: () => void;
  onSettled: () => void;
}

interface SettleFormModel {
  amount: number;
  note?: string;
}

const PREVIEW_ERROR_MESSAGES: Record<string, string> = {
  [PayoffErrorCode.AlreadyPaid]: "Ten plan jest już opłacony w całości.",
  // The offer window and the ±48h guard no longer refuse a settlement
  // (TRE-1745) — what is left here are the structural reasons.
  [PayoffErrorCode.NotEligible]:
    "Nie da się rozliczyć tego planu: albo trwa płatność online, albo nie ma go czym zamknąć " +
    "(brak subskrypcji w Stripe, nieczytelny harmonogram).",
};

/**
 * Things the operator should weigh before booking — none of them blocks the
 * settlement (TRE-1745).
 */
const WARNING_MESSAGES: Record<PayoffSettlementWarning, string> = {
  billing_window:
    "Rata wypada w ciągu 48h — sprawdź w Stripe, czy nie została już pobrana, zanim zaksięgujesz przelew.",
  subscription_not_active: "Subskrypcja nie jest aktywna (np. zaległa płatność) — dopłata i tak zamknie plan.",
  last_installment: "Została ostatnia rata — to nie jest wcześniejsza dopłata, tylko jej zapłata.",
  open_invoice: "W Stripe jest otwarta faktura na ratę — zostanie anulowana przy księgowaniu.",
  pricing_estimated:
    "Nie dało się odczytać salda z harmonogramu Stripe (np. kody rabatowe na fazach) — podpowiadana kwota to " +
    "nominał pozostałych rat bez rabatów. Wpisz kwotę, która faktycznie weszła na konto.",
};

const errorMessageOf = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { status?: number; data?: { error?: string; message?: string } } }).response;
  const code = response?.data?.error;

  if (code && PREVIEW_ERROR_MESSAGES[code]) {
    return PREVIEW_ERROR_MESSAGES[code];
  }

  // Without the status a 404 from a service that has not been deployed yet
  // looks exactly like a business rejection.
  return response?.data?.message || (response?.status ? `${fallback} (HTTP ${response.status})` : fallback);
};

const formatAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat("pl-PL", { style: "currency", currency: currency.toUpperCase() }).format(amount);

/**
 * Books a payoff the client paid by transfer (TRE-1737).
 *
 * Staff sees what the offer is worth today and confirms the amount that
 * actually landed on the account — the two differ often enough (an older
 * offer, a rounding, something agreed over the phone) that the suggestion has
 * to stay editable. Everything behind it is the same settlement the client
 * triggers herself from the payoff page: remaining installments cancelled,
 * order written, access marked paid.
 */
export const PayoffOfflineSettlementModal: FunctionComponent<OwnProps> = ({
  productClient,
  open,
  onClose,
  onSettled,
}) => {
  const [preview, setPreview] = useState<PayoffOfflineSettlementPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formController] = Form.useForm<SettleFormModel>();

  useEffect(() => {
    if (!open) {
      return;
    }

    setPreview(null);
    setError(null);
    formController.resetFields();

    // Reopening the modal quickly leaves the previous request in flight; the
    // older response must not land on top of the newer one and suggest an
    // amount from a different client.
    let stale = false;

    const load = async () => {
      setLoading(true);

      try {
        const { data } = await productApi.get<PayoffOfflineSettlementPreview>("/payoff/offline-settlement", {
          params: { productClientId: productClient.id },
        });

        if (stale) {
          return;
        }

        setPreview(data);
        formController.setFieldsValue({ amount: data.suggestedAmount });
      } catch (requestError) {
        if (!stale) {
          setError(errorMessageOf(requestError, "Nie udało się pobrać kwoty do rozliczenia."));
        }
      } finally {
        if (!stale) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      stale = true;
    };
  }, [open, productClient.id, formController]);

  const onSubmit = async (data: SettleFormModel) => {
    setSaving(true);
    setError(null);

    try {
      const { data: result } = await productApi.post<PayoffSettleOfflineResponse>("/payoff/settle-offline", {
        productClientId: productClient.id,
        amount: data.amount,
        note: data.note?.trim() || null,
      });

      void message.success(
        result.alreadySettled ? "Ten plan był już rozliczony" : "Dopłata zaksięgowana, raty anulowane",
      );
      onClose();
      onSettled();
    } catch (requestError) {
      setError(errorMessageOf(requestError, "Nie udało się zaksięgować dopłaty."));
    } finally {
      setSaving(false);
    }
  };

  // A products-service older than this admin answers without `warnings` —
  // reading `.length` off that is what crashed the whole client page.
  const warnings = preview?.warnings ?? [];

  const rewardDescription = () => {
    if (!preview) {
      return null;
    }

    if (preview.rewardType === "bonusTime") {
      return `pełna kwota + ${preview.bonusDays ?? 0} dni dostępu`;
    }

    if (preview.rewardType === "lastInstallmentFree") {
      return "ostatnia rata gratis";
    }

    return preview.discountPct > 0 ? `rabat ${preview.discountPct}%` : "bez rabatu";
  };

  return (
    <Modal
      title="Dopłata do całości — spłacone przelewem"
      open={open}
      okText="Zaksięguj dopłatę"
      onCancel={onClose}
      onOk={() => formController.submit()}
      okButtonProps={{ loading: saving, disabled: saving || loading || !preview }}
    >
      {loading ? <Skeleton active paragraph={{ rows: 3 }} /> : null}

      {error ? <Alert type="error" showIcon message={error} className="mb-4" /> : null}

      {warnings.length ? (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message={
            warnings.length === 1 ? (
              WARNING_MESSAGES[warnings[0]!]
            ) : (
              <ul className="m-0 pl-4">
                {warnings.map((warning) => (
                  <li key={warning}>{WARNING_MESSAGES[warning]}</li>
                ))}
              </ul>
            )
          }
        />
      ) : null}

      {preview ? (
        <>
          <Descriptions size="small" column={1} className="mb-4">
            <Descriptions.Item label="Pozostałe raty">{preview.remainingInstallments}</Descriptions.Item>
            <Descriptions.Item label="Pozostało do zapłaty">
              {formatAmount(preview.remainingAmount, preview.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Oferta">{rewardDescription()}</Descriptions.Item>
            {preview.accessUntil ? (
              <Descriptions.Item label="Dostęp po dopłacie">
                {dayjs(preview.accessUntil).format("DD.MM.YYYY")}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          <Form<SettleFormModel> form={formController} layout="vertical" onFinish={onSubmit}>
            <Form.Item
              label="Kwota, którą klientka wpłaciła"
              name="amount"
              tooltip="Podpowiadamy kwotę z oferty. Zmień ją, jeśli na konto weszła inna — zaksięgujemy tę wpisaną."
              rules={[
                { required: true, message: "Podaj kwotę" },
                {
                  type: "number",
                  min: 0.01,
                  max: preview.remainingAmount,
                  message: `Kwota musi mieścić się w pozostałym saldzie (${formatAmount(
                    preview.remainingAmount,
                    preview.currency,
                  )})`,
                },
              ]}
            >
              <InputNumber
                min={0.01}
                max={preview.remainingAmount}
                precision={2}
                decimalSeparator=","
                style={{ width: 220 }}
                addonAfter={preview.currency.toUpperCase()}
              />
            </Form.Item>
            <Form.Item label="Notatka (opcjonalnie)" name="note">
              <Input.TextArea rows={2} maxLength={500} placeholder="np. przelew z 30.07, tytuł: dopłata rat" />
            </Form.Item>
          </Form>

          <Alert
            type="info"
            showIcon
            message="Po zaksięgowaniu pozostałe raty zostaną anulowane, a plan oznaczony jako opłacony — dokładnie tak, jak przy płatności kartą na stronie dopłaty."
          />
        </>
      ) : null}
    </Modal>
  );
};
